import { ObjectToDraw, SpriteAnimData } from './../types';
import P5 from 'p5';
import Entity from './entity';
import SpriteAnimationRenderer from '../spriteAnimationRenderer';
import Player from '../game/player';
import Smoke from '../particles/smoke';
import TextParticle from '../particles/textParticle';
import GameMap from '../game/gameMap';
import GameItem from './gameItem';
import { moveWithCollisions } from '../game/collision';
import PathAi from '../pathFinding/pathAi';
import p5 from 'p5';

class Chicken implements Entity {
    NAME = 'chicken';

    // Tunable behavior constants - adjust here.
    static WANDER_TARGET_RETRIES = 5; // random points tried before giving up and idling a bit longer
    static FLEE_DISTANCE = 500; // how far the chicken tries to flee from the player when scared
    static FLEE_TRIGGER_DISTANCE = 100; // player closer than this scares the chicken into running
    static FLEE_END_DISTANCE = 200; // player must be further than this before the chicken stops running
    static FLEE_REPATH_DISTANCE = 100; // re-pick a flee target once this close to the current one
    static REPATH_INTERVAL_MS = 500; // recompute the flee path at least this often while running
    static FLEE_HOLD_MS = 2000; // forced running duration after being hit/knocked back
    static IDLE_WANDER_RANGE = 100; // random wander offset while idle
    static POST_FLEE_WANDER_RANGE = 256; // random wander offset right after fleeing ends
    static WANDER_ARRIVAL_DISTANCE = 20; // "close enough" to a wander goal
    static ARRIVAL_DISTANCE = 5; // "close enough" to a path waypoint / axis-snap tolerance
    static IDLE_DURATION_MIN_MS = 5000;
    static IDLE_DURATION_MAX_MS = 20000;
    static IDLE_RETRY_MIN_MS = 2000;
    static IDLE_RETRY_MAX_MS = 5000;

    hash: string;
    x: number;
    y: number;
    vx: number = 0;
    vy: number = 0;
    // Knockback impulse velocity, decays every frame (see knockback()).
    kbVx: number = 0;
    kbVy: number = 0;
    gx: number;
    gy: number;
    speed: number;
    currentDirection: number;
    state: string;
    oldState: string;
    startWalkingTime: number;
    endRunningTime: number;
    isDead: boolean;
    animIdle: SpriteAnimationRenderer;
    animRunning: SpriteAnimationRenderer;
    animWalking: SpriteAnimationRenderer;
    health: number;
    healthSmooth: number;
    hitbox: {w: number, h: number};
    strength: number;
    damagable: boolean;
    pathAi: PathAi;
    // Fallback flee direction used when pathfinding can't find a way away
    // from the player (see startFleeing()).
    fleeFallbackAngle: number = 0;
    // Next time (ms) the flee path is due to be recomputed (see startFleeing()).
    nextRepathTime: number = 0;

    constructor(private gameMap: GameMap, sx: number, sy: number) {
        this.hash = `chicken_${sx}_${sy}`;

        this.hitbox = {w: 32, h: 32};
        this.damagable = true;
        this.strength = 0.2;

        this.x = sx;
        this.y = sy;
        this.health = 100;
        this.healthSmooth = this.health;
        this.gx = 0;
        this.gy = 0;
        this.speed = 5;
        this.currentDirection = 7;
        this.state = 'idle';
        this.startWalkingTime = p5js.millis();
        this.isDead = false;
        this.pathAi = new PathAi(this.gameMap, 8);

        this.animIdle = new SpriteAnimationRenderer();
        this.animIdle.load('/assets/entities/chicken/idle');

        this.animRunning = new SpriteAnimationRenderer();
        this.animRunning.load('/assets/entities/chicken/running');

        this.animWalking = new SpriteAnimationRenderer();
        this.animWalking.load('/assets/entities/chicken/walking');
    }

    init() {
        console.log('Chicken initialized');
    }

    update() {
        let distance = p5js.dist(player.x, player.y, this.x, this.y)
        if(distance < Chicken.FLEE_TRIGGER_DISTANCE || this.endRunningTime > p5js.millis()){
            if(this.oldState !== 'running'){
                this.startFleeing();
            }
            this.state = 'running';
        }else if(distance > Chicken.FLEE_END_DISTANCE && this.state == 'running'){
            if(this.pickWanderTarget(Chicken.POST_FLEE_WANDER_RANGE)){
                this.state = 'walking';
            }else{
                this.state = 'idle';
                this.startWalkingTime = p5js.int(p5js.millis() + p5js.random(Chicken.IDLE_RETRY_MIN_MS, Chicken.IDLE_RETRY_MAX_MS));
            }
        }

        if(this.state == 'running'){
            // Getting close to the current flee point doesn't mean it's safe
            // - the player is still chasing, so pick a fresh point further
            // away and re-path. Also re-path periodically even when still far
            // from the goal: the player keeps moving and a path computed from
            // an earlier position can go stale or cut through walls that were
            // outside the earlier obstacle window.
            if(p5js.dist(this.x, this.y, this.gx, this.gy) < Chicken.FLEE_REPATH_DISTANCE
                || p5js.millis() > this.nextRepathTime){
                this.startFleeing();
            }

            let ngx = this.gx;
            let ngy = this.gy;

            if(this.pathAi.path.length > 1){
                // A route away from the player was found - follow it waypoint
                // by waypoint so the chicken doesn't run straight into a wall.
                for(let node of this.pathAi.path){
                    if(!node.explored){
                        if(p5js.dist(this.x, this.y, node.x, node.y) < Chicken.ARRIVAL_DISTANCE){
                            node.explored = true;
                        }else{
                            ngx = node.x;
                            ngy = node.y;
                            break;
                        }
                    }
                }
            }else{
                // Pathfinding couldn't find a route away from the player -
                // fall back to a fixed random direction. If that also runs
                // into a wall, moveWithCollisions below simply stops it there.
                ngx = this.x + p5js.cos(this.fleeFallbackAngle) * Chicken.FLEE_DISTANCE;
                ngy = this.y + p5js.sin(this.fleeFallbackAngle) * Chicken.FLEE_DISTANCE;
            }

            let angle = p5js.atan2(ngy - this.y, ngx - this.x);
            this.vx = p5js.int(p5js.cos(angle) * this.speed);
            this.vy = p5js.int(p5js.sin(angle) * this.speed);
            this.currentDirection = (p5js.int(p5js.map(p5js.atan2(this.vy, this.vx), -p5js.PI, p5js.PI, 0, 7))+3) % 8;
        }else if(this.state == 'walking'){
            let distanceToGoal = p5js.dist(this.x, this.y, this.gx, this.gy);

            if (distanceToGoal > Chicken.WANDER_ARRIVAL_DISTANCE) {
                // Reset velocities
                this.vx = 0;
                this.vy = 0;

                // Check x direction
                if (Math.abs(this.x - this.gx) < Chicken.ARRIVAL_DISTANCE) {
                    this.vx = 0;
                } else if (this.x < this.gx) {
                    this.vx = 1;
                } else {
                    this.vx = -1;
                }

                // Check y direction
                if (Math.abs(this.y - this.gy) < Chicken.ARRIVAL_DISTANCE) {
                    this.vy = 0;
                } else if (this.y < this.gy) {
                    this.vy = 1;
                } else {
                    this.vy = -1;
                }

                // Calculate the current direction
                this.currentDirection = (p5js.int(p5js.map(p5js.atan2(this.vy, this.vx), -p5js.PI, p5js.PI, 0, 7)) + 3) % 8;
            }else{
                this.startWalkingTime = p5js.int(p5js.millis() + p5js.random(Chicken.IDLE_DURATION_MIN_MS, Chicken.IDLE_DURATION_MAX_MS));
                this.state = 'idle';
                this.vx = 0;
                this.vy = 0;
            }
        }else if (this.state == 'idle'){
            this.vx = 0;
            this.vy = 0;

            if(p5js.millis() > this.startWalkingTime){
                if(this.pickWanderTarget(Chicken.IDLE_WANDER_RANGE)){
                    this.state = 'walking';
                }else{
                    // No reachable wander point found nearby - idle a bit
                    // longer instead of walking straight into a wall.
                    this.startWalkingTime = p5js.int(p5js.millis() + p5js.random(Chicken.IDLE_RETRY_MIN_MS, Chicken.IDLE_RETRY_MAX_MS));
                }
            }
        }

        //check if the mouse is over the chicken
        // let posX = p5js.int((p5js.mouseX/pixelSize) - (buffer.width/2));
        // let posY = p5js.int((p5js.mouseY/pixelSize) - (buffer.height/2));

        // if(p5js.dist(posX, posY, this.x-player.x, this.y-player.y) < 30){
        //     if(p5js.mouseIsPressed){
        //         player.score += 10;
        //         this.isDead = true;
        //     }
        // }

        // Move with wall collision (shared with the player), including any
        // active knockback impulse - so the chicken can't clip into walls.
        let moveResult = moveWithCollisions(this.gameMap, this.x, this.y, this.vx + this.kbVx, this.vy + this.kbVy, 5);
        this.x = moveResult.x;
        this.y = moveResult.y;

        // Decay the knockback impulse.
        this.kbVx *= 0.9;
        this.kbVy *= 0.9;
        if(p5js.max(p5js.abs(this.kbVx), p5js.abs(this.kbVy)) < 1){
            this.kbVx = 0;
            this.kbVy = 0;
        }

        if(this.oldState != this.state){
            if(this.state == 'running'){
                //particles.push(new Smoke(p5js, player, this.x, this.y, 3));
            }

        }
        this.oldState = this.state;
    }

    // Tries up to WANDER_TARGET_RETRIES random offsets within `range` px and
    // picks the first one whose straight line from the chicken isn't blocked
    // by a wall. Sets gx/gy and returns true on success; returns false (and
    // leaves gx/gy untouched) if every attempt crossed a wall.
    pickWanderTarget(range: number): boolean {
        for(let i = 0; i < Chicken.WANDER_TARGET_RETRIES; i++){
            let tx = this.x + p5js.int(p5js.random(-range, range));
            let ty = this.y + p5js.int(p5js.random(-range, range));

            this.pathAi.updateObstaclesFromChunks(this.x, this.y, tx, ty);
            if(!this.pathAi.lineIntersectsObstacles(this.x, this.y, tx, ty)){
                this.gx = tx;
                this.gy = ty;
                return true;
            }
        }
        return false;
    }

    // Called once when the chicken starts fleeing from the player. Picks a
    // point FLEE_DISTANCE px away from the player (in the direction away from
    // it) and tries to find a path to it that avoids walls. If no path can be
    // found, fleeFallbackAngle is used to just run in a random direction
    // instead (see update()).
    startFleeing() {
        let angle = p5js.atan2(this.y - player.y, this.x - player.x);
        this.gx = this.x + p5js.cos(angle) * Chicken.FLEE_DISTANCE;
        this.gy = this.y + p5js.sin(angle) * Chicken.FLEE_DISTANCE;

        this.pathAi.calculatePath(this.x, this.y, this.gx, this.gy);
        if(this.pathAi.path.length <= 1){
            this.fleeFallbackAngle = p5js.random(0, p5js.TWO_PI);
        }

        this.nextRepathTime = p5js.millis() + Chicken.REPATH_INTERVAL_MS;
    }

    render(objectsToDraw: ObjectToDraw[]) {
        let posOnScreen = {x: this.x-player.x, y: this.y-player.y};

        if(posOnScreen.x < -buffer.width/2-64 || posOnScreen.x > buffer.width/2+64 || posOnScreen.y < -buffer.height/2-64 || posOnScreen.y > buffer.height/2+64){
            return;
        }

        if(showDebug){
            buffer.stroke(255);
            buffer.strokeWeight(1);
            buffer.line(this.x-player.x, this.y-player.y, this.gx-player.x, this.gy-player.y);

            buffer.noStroke();
            buffer.fill(255, 0, 0);
            buffer.ellipse(this.gx-player.x, this.gy-player.y, 10, 10);

            buffer.fill(255);
            buffer.textAlign(p5js.CENTER, p5js.TOP);
            let time = p5js.int((this.startWalkingTime-p5js.millis())/1000);
            if(time < 0){
                time = 0;
            }
            buffer.text(this.state + ": " + time + "s", this.x-player.x, this.y-player.y+10);

            //draw the hitbox
            buffer.stroke(0, 0, 255);
            buffer.strokeWeight(1);
            buffer.noFill();
            buffer.rect(this.x-player.x-this.hitbox.w/2, this.y-player.y-this.hitbox.h, this.hitbox.w, this.hitbox.h);

            if(this.state == 'running'){
                this.pathAi.renderDebug();
            }
        }

        buffer.noStroke();
        buffer.fill(0, 100);
        buffer.ellipse(this.x-player.x, this.y-player.y, 30, 15);

        
        if(this.state == 'running'){
            this.animRunning.render(objectsToDraw, this.x-player.x, this.y-player.y, this.currentDirection);
        }else if(this.state == 'walking'){
            this.animWalking.render(objectsToDraw, this.x-player.x, this.y-player.y, this.currentDirection);
        }else if (this.state == 'idle'){
            this.animIdle.render(objectsToDraw, this.x-player.x, this.y-player.y, this.currentDirection);
        }

        
    }

    renderUI(): void {
        //draw health bar and name
        let offsetY = -50;
        let barWidth = 10;
        let healthWidth = p5js.map(this.health, 0, 100, -barWidth, barWidth);
        let healthSmoothWidth = p5js.map(this.healthSmooth, 0, 100, -barWidth, barWidth);
        buffer.stroke(0);
        buffer.strokeWeight(6);
        buffer.line(this.x-player.x-barWidth, this.y-player.y+offsetY, this.x-player.x+barWidth, this.y-player.y+offsetY);
        buffer.stroke(255);
        buffer.strokeWeight(4);
        buffer.line(this.x-player.x-barWidth, this.y-player.y+offsetY, this.x-player.x+healthSmoothWidth, this.y-player.y+offsetY);
        buffer.stroke(0, 255, 0);
        buffer.line(this.x-player.x-barWidth, this.y-player.y+offsetY, this.x-player.x+healthWidth, this.y-player.y+offsetY);
        this.healthSmooth = p5js.lerp(this.healthSmooth, this.health, 0.1);
        buffer.noStroke();
        buffer.fill(255);
        buffer.textAlign(p5js.CENTER, p5js.BOTTOM);
        buffer.textSize(10);
        buffer.text('Chicken', this.x-player.x, this.y-player.y+offsetY-5);
    }

    isToBeRemoved() {
        return this.isDead;
    }

    kill() {
        this.isDead = true;
    }

    hit(damage: number){
        let d = p5js.floor(damage / this.strength);
        this.health -= d
        particles.push(new TextParticle(this.x, this.y, `-${d}hp`));
        if(this.health <= 0){
            this.health = 0;
            particles.push(new Smoke(this.x, this.y, 10));

            //drop item
            let r = p5js.random(0, 1);
            if(r < 0.01){
                entities.push(new GameItem("egg", this.x, this.y, p5js.random(1, 5)));
            }

            this.kill();
        }else{
            particles.push(new Smoke(this.x, this.y, 3));
        }

        //make the chicken run away
        this.state = 'running';
        this.endRunningTime = p5js.millis() + Chicken.FLEE_HOLD_MS;
    }

    knockback(x: number, y: number){
        this.kbVx = x;
        this.kbVy = y;

        //getting kicked around scares the chicken
        this.state = 'running';
        this.endRunningTime = p5js.millis() + Chicken.FLEE_HOLD_MS;
    }

    getHash() {
        return this.hash;
    }

    isInHitbox(px: number, py: number): boolean {
        return px > this.x - this.hitbox.w/2 && px < this.x + this.hitbox.w/2 && py > this.y - this.hitbox.h && py < this.y;
    }
}

export default Chicken;